/**
 * POPSTAR LIVE — placeholder screen (presentational).
 *
 * In legacy Avoid!, POPSTAR LIVE never actually shipped: `POPSTAR_ENTER_LIVE_DONE()`
 * just traced `"LIVE ENTER DONE"` and wired nav listeners — no replay capture, no
 * feed (docs/legacy/AVOID_LEGACY_ANALYSIS.md §2.11). This screen is the honest
 * "coming soon" placeholder for the reworked, server-judged version (plan P7),
 * and it doubles as in-app documentation of HOW the replay system will work.
 *
 * THE REPLAY APPROACH (the keystone idea, docs/BARRAGE_IMPLEMENTATION_PLAN.md
 * "one deterministic sim, three jobs"): a run is NOT stored as video. Every run
 * submits a tiny {@link import('../core/types').RunRecord} — just a `seed` plus
 * the player's `inputs` timeline (a few KB). Because `core/` is a deterministic
 * simulation (seeded RNG + fixed timestep, no wall-clock / no Math.random),
 * re-running that same `{seed, inputs}` reproduces the run byte-for-byte. The
 * SAME mechanism powers three things:
 *   1. anti-cheat — the Cloud Function re-runs the record to get the
 *      authoritative score (the client's claimed score is never trusted);
 *   2. replays — POPSTAR LIVE re-runs a stored {@link import('../services/dataModel').RunDoc}
 *      in the browser to play it back;
 *   3. shared-seed multiplayer — everyone in a match shares one seed, so they all
 *      face identical obstacles.
 * Replay = anti-cheat = multiplayer, all the same re-simulation. That's why a
 * `runs/{runId}` doc only needs `{seed, inputs}` to recreate any moment.
 *
 * DESIGN: POPSTAR is the glamorous, showbiz half of the app, so LIVE is dressed
 * as a stage — a red theatre curtain framing a dim spotlit stage that reads
 * "Coming soon — replays of recent runs". The frosted-glass marquee, jobs, and
 * "up next" list all sit on that stage. Palette/blur/radius come from the
 * POPSTAR re-map in src/index.css; only the curtain + stage layout is scoped here.
 *
 * ARCHITECTURE: presentational only — no state, no service/Firebase/game imports
 * beyond read-only data shapes. The shell decides when to show it; this just
 * explains the plan (and, when given, teases the featured runs that will become
 * playable). Nothing here is wired to a real replay player yet.
 */
import type { LeaderboardEntry } from '../services/dataModel';
import { useT } from './i18n';

interface LiveScreenProps {
  /**
   * Optional preview of recent/featured runs that WILL become playable replays.
   * Rendered as a disabled "coming soon" list (no replay player exists yet). When
   * omitted, only the explainer shows. These map to `runs/{runId}` docs via the
   * leaderboard's `runId`.
   */
  featured?: LeaderboardEntry[];
  /** Back to the previous screen (menu / network shell). */
  onBack: () => void;
  /** When true (inside the POPSTAR tab shell) suppress this screen's own footer-back. */
  embedded?: boolean;
}

/** The three jobs one RunRecord does — drawn as a little explainer grid. */
const REPLAY_JOBS: ReadonlyArray<{ icon: string; title: string; body: string }> = [
  {
    icon: '🛡️',
    title: 'Anti-cheat',
    body: 'The server re-runs your {seed, inputs} to compute the real score. Faked scores never match.',
  },
  {
    icon: '🎬',
    title: 'Replays',
    body: 'LIVE re-runs a stored record in your browser — a few KB recreates the whole run, no video.',
  },
  {
    icon: '⚔️',
    title: 'Multiplayer',
    body: 'Everyone shares one seed, so all players face the exact same barrage. Fair and cheap.',
  },
];

export function LiveScreen({ featured, onBack, embedded = false }: LiveScreenProps) {
  const t = useT();
  return (
    <div className={'screen live-screen' + (embedded ? ' is-embedded' : '')}>
      <style>{LIVE_CSS}</style>

      {/* The theatre: a red curtain framing a dim, spotlit stage. */}
      <div className="live-stage">
        <div className="live-curtain live-curtain-left" aria-hidden="true" />
        <div className="live-curtain live-curtain-right" aria-hidden="true" />
        <div className="live-valance" aria-hidden="true" />
        <div className="live-spotlight" aria-hidden="true" />

        <header className="live-marquee">
          <span className="live-onair">
            <span className="live-onair-dot" aria-hidden="true" />
            {t('live.onAir')}
          </span>
          <h1 className="title">{t('live.title')}</h1>
          <p className="subtitle">{t('live.sub')}</p>
        </header>
      </div>

      <section className="panel live-explainer-panel">
        <h2>How replays will work</h2>
        <p className="live-explainer">
          A run isn't saved as a video. We store a tiny record — a random{' '}
          <strong>seed</strong> plus your <strong>input timeline</strong> (a few
          kilobytes). Because the game's simulation is fully deterministic, replaying
          that <code>{'{ seed, inputs }'}</code> recreates the run exactly. LIVE will
          re-run those records right in the browser, so you can watch any leaderboard
          run frame-for-frame.
        </p>

        <div className="job-grid">
          {REPLAY_JOBS.map((job) => (
            <div key={job.title} className="job-card">
              <span className="job-icon" aria-hidden="true">
                {job.icon}
              </span>
              <span className="job-title">{job.title}</span>
              <span className="job-body">{job.body}</span>
            </div>
          ))}
        </div>
      </section>

      {featured && featured.length > 0 && (
        <section className="panel live-featured">
          <h2>Tonight's lineup</h2>
          <ol className="leaderboard">
            {featured.map((run, i) => (
              <li key={run.runId || `${run.uid}-${i}`} className="leaderboard-row live-run">
                <span className="rank">{i + 1}</span>
                <span className="name">{run.name}</span>
                <span className="score">{run.score.toLocaleString()}</span>
                <span className="live-soon" aria-label="Replay coming soon">
                  ▶ soon
                </span>
              </li>
            ))}
          </ol>
          <p className="live-foot">Replay playback takes the stage in a later update.</p>
        </section>
      )}

      {!embedded && (
        <button className="btn btn-ghost" onClick={onBack}>
          Back
        </button>
      )}
    </div>
  );
}

/**
 * Screen-scoped styles (nested under `.live-screen`). Shared primitives
 * (`.screen`, `.panel`, `.btn`, `.title`, `.leaderboard`, CSS vars + the POPSTAR
 * palette re-map) come from src/index.css; only the LIVE stage/curtain layout
 * lives here. Curtain colours are derived from the showbiz red-accent tokens so
 * they stay in sync with the rest of the network.
 */
const LIVE_CSS = `
/* The stage: a glass slab that frames the marquee, draped with red curtains. */
.live-screen .live-stage {
  position: relative;
  width: min(440px, 92vw);
  margin-top: 4px;
  padding: 56px 24px 40px;
  border-radius: var(--radius-lg);
  overflow: hidden;
  isolation: isolate;
  background:
    radial-gradient(120% 90% at 50% -10%, rgba(255, 79, 157, 0.18), transparent 60%),
    linear-gradient(180deg, rgba(28, 14, 46, 0.9), rgba(18, 8, 34, 0.92));
  border: 1px solid var(--glass-border-strong);
  box-shadow: var(--shadow-glass);
  backdrop-filter: blur(var(--blur));
  -webkit-backdrop-filter: blur(var(--blur));
}

/* Two heavy velvet drapes pulled to the sides, with a soft pleated sheen. */
.live-screen .live-curtain {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 22%;
  z-index: 1;
  pointer-events: none;
  background-color: #8e0e3a;
  background-image:
    repeating-linear-gradient(
      90deg,
      rgba(0, 0, 0, 0.36) 0,
      rgba(0, 0, 0, 0) 7px,
      rgba(255, 255, 255, 0.12) 14px,
      rgba(0, 0, 0, 0) 21px,
      rgba(0, 0, 0, 0.36) 28px
    ),
    linear-gradient(180deg, #b81350, #6e0a2c);
  box-shadow: 0 0 24px rgba(0, 0, 0, 0.45);
}
.live-screen .live-curtain-left {
  left: 0;
  border-top-left-radius: var(--radius-lg);
  border-bottom-right-radius: 60% 24px;
}
.live-screen .live-curtain-right {
  right: 0;
  border-top-right-radius: var(--radius-lg);
  border-bottom-left-radius: 60% 24px;
}

/* Pelmet / valance across the top, gathered in soft swags with a gold trim. */
.live-screen .live-valance {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 30px;
  z-index: 2;
  pointer-events: none;
  background:
    radial-gradient(20px 26px at 14px top, #b81350 60%, transparent 62%) 0 0 / 28px 100% repeat-x,
    linear-gradient(180deg, #b81350, #7a0c30);
  border-bottom: 2px solid var(--gold-deep);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4);
}

/* A dim spotlight cone washing the centre of the stage. */
.live-screen .live-spotlight {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: radial-gradient(
    60% 70% at 50% 22%,
    rgba(255, 240, 200, 0.16),
    transparent 70%
  );
}

/* The marquee sits center-stage above the spotlight, below the drapes' edges. */
.live-screen .live-marquee {
  position: relative;
  z-index: 3;
  text-align: center;
  margin: 0 auto;
  max-width: 80%;
}
.live-screen .live-marquee .title {
  font-size: clamp(34px, 10vw, 60px);
  margin: 6px 0 0;
}
.live-screen .live-marquee .subtitle {
  margin-top: 10px;
}

/* "On air soon" — a marquee badge with a soft pulsing bulb. */
.live-screen .live-onair {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--gold);
  background: rgba(255, 209, 102, 0.12);
  border: 1px solid rgba(255, 209, 102, 0.34);
  padding: 6px 14px;
  border-radius: var(--radius-pill);
  box-shadow: inset 0 1px 0 var(--glass-hi);
}
.live-screen .live-onair-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent-2);
  box-shadow: 0 0 8px var(--accent-2);
  animation: pulse-dot 2.4s ease-out infinite;
}

/* Explainer panel + jobs grid. */
.live-screen .live-explainer-panel { width: min(440px, 92vw); }
.live-screen .live-explainer {
  color: var(--muted);
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 14px;
}
.live-screen .live-explainer code {
  background: var(--panel-2);
  border-radius: 6px;
  padding: 1px 6px;
  font-size: 13px;
  color: var(--accent-soft);
}
.live-screen .live-explainer strong { color: var(--text); }
.live-screen .job-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}
.live-screen .job-card {
  display: grid;
  grid-template-columns: 28px 1fr;
  grid-template-rows: auto auto;
  column-gap: 10px;
  align-items: center;
  background: var(--panel-2);
  border: 1px solid var(--glass-border);
  border-radius: 12px;
  padding: 12px 14px;
  transition: border-color var(--t) var(--ease), background var(--t) var(--ease);
}
.live-screen .job-card:hover {
  border-color: var(--accent);
  background: var(--accent-tint);
}
.live-screen .job-icon { grid-row: 1 / 3; font-size: 22px; }
.live-screen .job-title { font-weight: 700; font-size: 14px; }
.live-screen .job-body {
  grid-column: 2;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.4;
}

/* "Tonight's lineup" — the runs queued to become playable replays. */
.live-screen .live-featured { width: min(440px, 92vw); }
.live-screen .live-run {
  grid-template-columns: 28px 1fr auto auto;
  opacity: 0.82;
}
.live-screen .live-soon {
  font-size: 12px;
  color: var(--gold);
  font-weight: 700;
  letter-spacing: 0.04em;
}
.live-screen .live-foot {
  margin: 10px 0 0;
  font-size: 12px;
  color: var(--muted);
  text-align: center;
}
`;
